/**
 * JS-readable auth cookie helpers (decision D-006, glowtest-aligned).
 *
 * Replaces the previous localStorage storage for `auth_token` and
 * `raisy_firebase_user`. Mirrors the js-cookie semantics used by
 * glowtest_FE/src/auth/authenticateSlide.js: Cookies.set(name, value, { expires: 30 }):
 *   - SameSite=Lax, Path=/, 30-day expiry
 *   - NO Secure flag, so auth keeps working on http://localhost dev
 *   - values encoded with encodeURIComponent (same default as js-cookie)
 *
 * Written by hand because js-cookie is not a raisy_FE dependency.
 */

export const AUTH_TOKEN_COOKIE = "auth_token";
export const AUTH_USER_COOKIE = "raisy_firebase_user";

const DEFAULT_EXPIRY_DAYS = 30;
const BASE_ATTRIBUTES = "path=/; samesite=lax";

function isBrowser(): boolean {
  return typeof document !== "undefined";
}

export function setAuthCookie(
  name: string,
  value: string,
  expiresDays: number = DEFAULT_EXPIRY_DAYS,
): void {
  if (!isBrowser()) return;
  const expires = new Date(
    Date.now() + expiresDays * 24 * 60 * 60 * 1000,
  ).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; ${BASE_ATTRIBUTES}`;
}

export function getAuthCookie(name: string): string | null {
  if (!isBrowser()) return null;
  const prefix = `${name}=`;
  for (const chunk of document.cookie.split("; ")) {
    if (chunk.startsWith(prefix)) {
      try {
        return decodeURIComponent(chunk.slice(prefix.length));
      } catch {
        return chunk.slice(prefix.length);
      }
    }
  }
  return null;
}

export function removeAuthCookie(name: string): void {
  if (!isBrowser()) return;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; ${BASE_ATTRIBUTES}`;
}

/** Store a JSON-serialisable record (e.g. the firebase user snapshot). */
export function setAuthJsonCookie(name: string, value: unknown): void {
  setAuthCookie(name, JSON.stringify(value));
}

export function getAuthJsonCookie<T>(name: string): T | null {
  const raw = getAuthCookie(name);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}
